import { isObject } from 'lib';
import { NATIVE_PLATFORMS, mapPlatformToPath } from 'utils';
import {
  getCategoryNativePath,
  getResourceId,
} from 'pages/resources/utils/resourcePlatforms';

export const SEARCH_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

export const NAME_ERROR = {
  REQUIRED: 'required',
  DUPLICATE: 'duplicate',
};

export const isDuplicateNameError = (error) =>
  Boolean(error?.response?.data?.errors?.data?.errors?.name);

/**
 * The per-user system tags of the v4 API. They are addressed by these names
 * in place of a numeric tag id.
 */
export const SYSTEM_TAG = {
  BLACKLIST: 'blacklist',
  VIP: 'vip',
};

export const isSystemTag = (id) => Object.values(SYSTEM_TAG).includes(id);

export const TAG_ERROR_CODE = {
  INVALID_SOURCES: 'invalid_sources',
  MAX_ALLOWED: 'permission.max_allowed',
};

const getTagErrorData = (error) => error?.response?.data?.errors?.data;

export const getTagErrorCode = (error) => getTagErrorData(error)?.error_code;

/** Items the v4 API could not resolve, as they were sent. */
export const getTagFailedItems = (error) => getTagErrorData(error)?.failed;

export const isTagCapacityError = (error) =>
  getTagErrorCode(error) === TAG_ERROR_CODE.MAX_ALLOWED;

export const RESOURCE_FETCH_TARGET = {
  LIST: 'list',
  MANAGE: 'manage',
};

const isNativePlatform = (platform) => NATIVE_PLATFORMS.includes(platform);

export const getCategoryApiPath = (platform) =>
  mapPlatformToPath(isNativePlatform(platform) ? 'nativePlatforms' : platform);

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

/**
 * Tag rows often wrap the channel. The wrapper keeps the counts from when
 * the source was added. The nested resource has the current ones.
 */
export const adaptCategoryResource = (item) => {
  const resource = isObject(item?.resource) ? item.resource : {};
  return {
    ...item,
    ...resource,
    id: firstDefined(
      resource.id,
      item?.id,
      resource.channel_id,
      item?.channel_id
    ),
    title: firstDefined(
      resource.title,
      item?.title,
      resource.name,
      item?.name,
      resource.full_name,
      item?.full_name,
      resource.username,
      item?.username
    ),
    username: firstDefined(
      resource.username,
      item?.username,
      resource.screen_name,
      item?.screen_name
    ),
    avatar: firstDefined(resource.avatar, item?.avatar),
    description: firstDefined(resource.description, item?.description),
    member_count: firstDefined(resource.member_count, item?.member_count),
    influence_category: firstDefined(
      resource.influence_category,
      item?.influence_category
    ),
    follower_count: firstDefined(resource.follower_count, item?.follower_count),
    follower: firstDefined(resource.follower, item?.follower),
    post_count: firstDefined(resource.post_count, item?.post_count),
    public_metrics: resource.public_metrics || item?.public_metrics,
  };
};

/**
 * A v4 tag-resource row. `id` is the row inside the tag; the resource itself
 * is `entity_id`, which is also what removal matches against.
 */
export const adaptTagResource = (row) => ({
  ...row,
  rowId: row?.id,
  id: row?.entity_id,
  title: row?.resource_title ?? row?.username,
  avatar: row?.resource_image,
});

export const getCategoryEntryKey = (platform, item) =>
  `${platform}:${adaptCategoryResource(item).id}`;

export const toCategoryEntry = (platform, item, extra = {}) => ({
  key: getCategoryEntryKey(platform, item),
  platform,
  item: adaptCategoryResource(item),
  isLocal: false,
  ...extra,
});

/**
 * Count of one tab from the tag object's `platforms`.
 * Bale, Eitaa and Rubika share `iranian-platforms-channels` when that value
 * is a single number.
 */
const readCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : undefined;
};

export const getInfoPlatformCount = (infoCounts, platform) => {
  if (!isObject(infoCounts)) return undefined;
  if (isNativePlatform(platform)) {
    const channelKey = `${platform}_channel`;
    const grouped = infoCounts[getCategoryApiPath('nativePlatforms')];
    if (isObject(grouped)) {
      return readCount(grouped[channelKey] ?? grouped[platform]);
    }
    return readCount(grouped ?? infoCounts[channelKey]);
  }
  return readCount(infoCounts[getCategoryApiPath(platform)]);
};

/**
 * Source limit of one tab from a system tag's `platform_limits`. Bale, Eitaa
 * and Rubika share the `iranian-platforms-channels` limit.
 */
export const getPlatformLimit = (platformLimits, platform) =>
  platformLimits?.[getCategoryApiPath(platform)]?.limit;

/** Sum each platforms key once. A nested native map is summed, not repeated. */
export const sumInfoPlatformCounts = (infoCounts) => {
  if (!isObject(infoCounts)) return 0;
  return Object.values(infoCounts).reduce((sum, value) => {
    if (isObject(value)) {
      return (
        sum +
        Object.values(value).reduce(
          (inner, item) => inner + (readCount(item) || 0),
          0
        )
      );
    }
    return sum + (readCount(value) || 0);
  }, 0);
};

/**
 * v4 platform of one tab. Native tabs use their sub-platform
 * (`bale-channels`), so lists and adds are scoped to that messenger.
 */
export const getTagPlatform = (platform) =>
  getCategoryNativePath(platform) || getCategoryApiPath(platform);

export const getCategoryResourceParams = (platform) => ({
  platform: getTagPlatform(platform),
  frontPlatform: platform,
});

export const toTagResourceItem = (platform, item) => ({
  platform: getTagPlatform(platform),
  source_id: String(getResourceId(item)),
});

/**
 * Applies one fetched page of a tab. Page 1 replaces the tab, later pages
 * are appended without the rows already shown.
 */
export const mergeManagePage = ({
  resources,
  totals,
  pages,
  frontPlatform,
  page = 1,
  data,
  total,
}) => {
  const current = page === 1 ? [] : resources[frontPlatform] || [];
  const known = new Set(current.map(({ key }) => key));
  const fresh = (data || [])
    .map((item) => toCategoryEntry(frontPlatform, item))
    .filter(({ key }) => !known.has(key));
  return {
    resources: { ...resources, [frontPlatform]: [...current, ...fresh] },
    totals: {
      ...totals,
      [frontPlatform]: total ?? current.length + fresh.length,
    },
    pages: { ...pages, [frontPlatform]: page },
  };
};
